/* eslint-disable max-lines */
import {
  CaptionScriptixDescendant,
  DocumentSubtitleSegmentV1
} from '@utils/types/editors/subtitles/subtitle.ts';
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  Slate,
  withReact
} from 'slate-react';
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { HistoryEditor, withHistory } from 'slate-history';
import {
  createEditor,
  Descendant,
  Editor,
  Node,
  Range,
  Transforms
} from 'slate';
import {
  isAtStartOfNode,
  withErrorHandling
} from '@utils/extensions/slate-editor-utils.ts';
import { RenderLeafProps } from 'slate-react/dist/components/editable';
import { getSelectedWordFromSelection } from '@utils/editors/get-selected-word-from-selection.ts';
import { useAudio } from '@utils/providers/audio-context.tsx';
import cn from 'classnames';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { useSharedCaptionEditorContext } from '@utils/providers/shared-caption-editor-context.tsx';
import { SharedStartStopTimeInputs } from '@components/editors/shared-editor/shared-caption/shared-start-stop-time-inputs.tsx';
import { SharedCaptionSegmentEditingTools } from '@components/editors/shared-editor/shared-caption/shared-caption-segment-editing-tools.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

/* eslint-disable no-restricted-syntax -- Semantic editor colors (recording/streaming status, playback cues, subtitle palette, pill foregrounds on tinted backgrounds) must stay stable across themes */

type Props = {
  index: number;
  segment: DocumentSubtitleSegmentV1;
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
  filteredItems: DocumentSubtitleSegmentV1[];
};

const LINE_LIMIT = 40;
const BLOCK_LIMIT = 80;

const SharedScriptixCaptionEditor = ({
  segment,
  onWordOutOfView,
  onWordInView,
  index,
  filteredItems
}: Props) => {
  const [editor] = useState(() =>
    withErrorHandling(withHistory(withReact(createEditor())))
  );
  const {
    setEditor,
    setClickedIndex,
    editorUpdateSegment,
    undo,
    redo,
    historyVersion,
    document: captionDocument
  } = useSharedCaption();
  const { addEditor, removeEditor, trackModification } =
    useSharedCaptionEditorContext();
  const segmentRef = useRef<HTMLDivElement | null>(null);
  const { theme } = useThemeContext();

  // Theme-aware colors
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const borderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
  const pastTextColor = pick('#000000', '#e5e5e5'); // black / neutral-200
  const futureTextColor = pick('#707070', '#a3a3a3'); // gray / neutral-400

  useEffect(() => {
    addEditor(index.toString(), editor);
    return () => {
      removeEditor(index.toString());
    };
  }, [index, editor]);

  // Undo/redo replace the document; show the restored text in place so focus
  // and the other segments are untouched. Only runs after undo/redo, never
  // while typing, so it can't race keystrokes. Reads the context document:
  // the `segment` prop arrives a render later through the virtualised list.
  useEffect(() => {
    if (!historyVersion || !segment) return;
    const restored = captionDocument?.document.find(s => s.id === segment.id);
    if (!restored) return;
    const text = restored.text ?? '';
    if (Node.string(editor) === text) return;
    HistoryEditor.withoutSaving(editor as HistoryEditor, () => {
      Transforms.insertText(editor, text, {
        at: { anchor: Editor.start(editor, []), focus: Editor.end(editor, []) }
      });
    });
  }, [historyVersion]);

  const initialValue = useMemo(() => {
    if (!segment) return [];
    return [
      {
        id: segment.id,
        start: segment.start,
        stop: segment.stop,
        position: segment.position ?? null,
        bold: segment.bold ?? false,
        italic: segment.italic ?? false,
        underlined: segment.underlined ?? false,
        strike: segment.strike ?? false,
        color: segment.color ?? '',
        children: [{ text: segment.text || '' }]
      } as CaptionScriptixDescendant
    ];
  }, [segment]);

  const { setTimeAndSegment } = useSharedCaption();
  const { currentTime, setTime, linkedPlayback } = useAudio();
  const linkedRef = useRef(linkedPlayback);
  linkedRef.current = linkedPlayback;

  // Apply 50ms shift for visual highlighting consistency
  const currentTimeMs = currentTime * 1000;
  const shiftedTimeMs = currentTimeMs + 50;
  const isCurrentWord = segment
    ? shiftedTimeMs >= segment.start && shiftedTimeMs <= segment.stop
    : false;

  const [plainText, setPlainText] = useState<string>(segment?.text || '');
  useEffect(() => {
    setPlainText(segment?.text || '');
  }, [segment?.id]); // reset when segment changes

  const lines = useMemo(() => plainText.split('\n'), [plainText]);
  const lineLengths = useMemo(() => lines.map(l => l.length), [lines]);
  const blockChars = useMemo(
    () => plainText.replace(/\n/g, '').length,
    [plainText]
  );

  const handleSelect = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      if (!linkedRef.current) return;
      const selectedWordElement = getSelectedWordFromSelection(editor);
      if (selectedWordElement) {
        setTimeAndSegment(Number(selectedWordElement));
        setTime(selectedWordElement.start / 1000);
      }
    },
    [editor, setTimeAndSegment, setTime]
  );

  const spanStyle = useMemo(
    () => ({
      color: (() => {
        if (isCurrentWord) return '#0a8df8';
        if (segment.stop && segment.stop <= shiftedTimeMs) return pastTextColor;
        if (segment.start && segment.start > shiftedTimeMs)
          return futureTextColor;
        return 'unset';
      })(),
      // Text styling (bold, italic, etc.) is only shown in video preview, not in editor
      fontStyle: 'normal',
      textDecoration: 'none',
      fontWeight: 'normal',
      fontSize: '16px'
    }),
    [isCurrentWord, segment, shiftedTimeMs, pastTextColor, futureTextColor]
  );

  useEffect(() => {
    if (!linkedPlayback) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isCurrentWord) {
          if (!entry.isIntersecting) {
            const direction =
              entry.rootBounds &&
              entry.boundingClientRect.top < entry.rootBounds.top
                ? 'up'
                : 'down';
            onWordOutOfView(segmentRef.current, direction);
          } else {
            onWordInView();
          }
        }
      },
      { root: null, threshold: 0.1 }
    );

    if (segmentRef.current) {
      observer.observe(segmentRef.current);
    }

    return () => {
      if (segmentRef.current) {
        observer.unobserve(segmentRef.current);
      }
    };
  }, [isCurrentWord, onWordOutOfView, onWordInView, linkedPlayback]);

  const handleItemClick = (seg: DocumentSubtitleSegmentV1) => {
    setClickedIndex(seg.id);
    if (linkedRef.current) {
      setTime(seg.start / 1000);
    }
  };

  const handleMouseUp = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => handleSelect(e),
    [handleSelect]
  );

  const handleChange = (children: Descendant[]) => {
    editorUpdateSegment(children);
    trackModification(editor);
    const text = Node.string(editor);
    setPlainText(text);
  };
  const renderElement = useCallback(
    ({ attributes, children, element }: RenderElementProps) => {
      const { id, start, stop } = element as CaptionScriptixDescendant;

      return (
        <div ref={segmentRef}>
          <div className="flex items-start w-full" key={id}>
            <div
              className="flex justify-between h-full w-full"
              onClick={e => {
                e.stopPropagation();
                window.getSelection?.()?.removeAllRanges();
              }}
            >
              <span
                key={`${start}-${stop}-${id}`}
                ref={segmentRef}
                className={cn(
                  'flex cursor-text relative w-full',
                  'h-[92px]',
                  'overflow-y-hidden',
                  'box-border',
                  'transition-all duration-200'
                )}
                style={{ borderColor, outline: 'none', boxShadow: 'none' }}
                data-start={start}
                data-stop={stop}
                data-index={id}
                id={`caption-${id}`}
                onClick={() => handleItemClick(segment)}
              >
                {/* Start/Stop inputs - 26% - start aligned */}
                <div
                  className="select-none min-h-full flex items-stretch justify-start w-[26%]"
                  contentEditable={false}
                  suppressContentEditableWarning
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                >
                  <SharedStartStopTimeInputs
                    segment={segment}
                    filteredItems={filteredItems}
                    counters={{
                      lineLengths,
                      lineLimit: LINE_LIMIT,
                      blockChars,
                      blockLimit: BLOCK_LIMIT
                    }}
                  />
                </div>

                {/* Editor content - 70% - center aligned */}
                <div
                  className="w-[70%] h-full overflow-y-auto break-words text-center flex justify-center items-center px-2"
                  {...attributes}
                >
                  {children}
                </div>

                {/* Editing tools — pinned to the top-right of the row.
                    Matches the private caption editor: the cluster
                    stacks vertically (Plus, Split, kebab menu). */}
                <div
                  className="select-none flex items-start justify-end w-[4%]"
                  contentEditable={false}
                  suppressContentEditableWarning
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  style={{
                    visibility: ReactEditor.isFocused(editor)
                      ? 'visible'
                      : 'hidden'
                  }}
                >
                  <SharedCaptionSegmentEditingTools
                    segment={segment}
                    filteredItems={filteredItems}
                  />
                </div>
              </span>
            </div>
          </div>
        </div>
      );
    },
    [filteredItems, segment, lineLengths, blockChars, borderColor]
  );

  const renderLeaf = useCallback(
    ({ attributes, children }: RenderLeafProps) => (
      <span
        data-start={segment?.start}
        data-stop={segment?.stop}
        onMouseUp={handleMouseUp}
        style={spanStyle}
        {...attributes}
      >
        {children}
      </span>
    ),
    [spanStyle, segment, handleMouseUp]
  );

  // Early return if segment is undefined
  if (!segment) return null;

  return (
    <Slate
      key={`${segment.id}-${segment.start}-${segment.stop}`}
      editor={editor}
      initialValue={initialValue}
      onChange={handleChange}
    >
      <Editable
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        style={{
          outline: 'none',
          padding: '0em 0em',
          border: 'none',
          boxShadow: 'none'
        }}
        className="no-outline"
        onKeyDown={event => {
          if (event.ctrlKey || event.metaKey) {
            const k = event.key.toLowerCase();

            // Undo/redo always use the document history, which holds text
            // edits as well as insert/split/delete/timing, so Ctrl+Z undoes
            // the latest change wherever it was made.
            if (k === 'z' && !event.shiftKey) {
              event.preventDefault();
              event.stopPropagation();
              undo();
              return;
            }
            if ((k === 'z' && event.shiftKey) || k === 'y') {
              event.preventDefault();
              event.stopPropagation();
              redo();
              return;
            }
            switch (k) {
              // Ctrl/Cmd +, = and - stay with the browser (page zoom).
              case 'f':
              case 'b':
              case 'i':
              case 'u':
              case 's':
                event.preventDefault();
                return;
            }
          } else if (event.key === 'ArrowLeft' || event.key === 'Backspace') {
            const { selection } = editor;
            const isCollapsedAtStart = isAtStartOfNode(editor);
            if (
              selection &&
              isCollapsedAtStart &&
              Range.isCollapsed(selection)
            ) {
              event.preventDefault();
            }
          } else if (event.key === 'Enter') {
            // keep your single-block policy: insert a newline
            event.preventDefault();
            editor.insertText('\n');
          }
        }}
        onFocus={() => setEditor(editor)}
      />
    </Slate>
  );
};

export default SharedScriptixCaptionEditor;
